/**
 * AI Chat Page
 * Modern chat interface for Tina health assistant with i18n support
 */

import React, { useState, useEffect, useRef } from "react";
import {
  Send,
  Bot,
  User,
  Loader2,
  Plus,
  MessageSquare,
  Trash2,
  Brain,
  BrainCircuit,
} from "lucide-react";
import { useLocaleFormat } from "@/hooks/useLocaleFormat";
import { useI18nStore } from "@/stores/i18nStore";
import {
  chatService,
  type Message,
  type Conversation,
} from "@/services/api/chatService";
import toast from "react-hot-toast";

export const ChatPage: React.FC = () => {
  const { t, language } = useI18nStore();
  const fmt = useLocaleFormat();

  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [currentConversation, setCurrentConversation] = useState<string | null>(
    null,
  );
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputMessage, setInputMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [includeHealthContext, setIncludeHealthContext] = useState(true);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    loadConversations();
  }, []);

  useEffect(() => {
    if (currentConversation) {
      loadConversation(currentConversation);
    }
  }, [currentConversation]);

  const loadConversations = async () => {
    try {
      setIsLoading(true);
      const convs = await chatService.getConversations();
      setConversations(convs);
    } catch (error: any) {
      console.error("Failed to load conversations:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadConversation = async (conversationId: string) => {
    try {
      setIsLoading(true);
      const { messages: convMessages } =
        await chatService.getConversation(conversationId);
      setMessages(convMessages);
    } catch (error: any) {
      console.error("Failed to load conversation:", error);
      toast.error("Failed to load conversation");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!inputMessage.trim() || isSending) return;

    const userMessage = inputMessage.trim();
    setInputMessage("");
    setIsSending(true);

    const tempUserMessage: Message = {
      conversationId: currentConversation || "temp",
      role: "user",
      content: userMessage,
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, tempUserMessage]);

    try {
      const response = await chatService.sendMessage({
        message: userMessage,
        language: language,
        includeHealthContext,
        ...(currentConversation && { conversationId: currentConversation }),
      });

      if (!currentConversation) {
        setCurrentConversation(response.conversationId);
        await loadConversations();
      }

      const aiMessage: Message = {
        conversationId: response.conversationId,
        role: "assistant",
        content: response.message,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, aiMessage]);
    } catch (error: any) {
      console.error("Failed to send message:", error);
      setMessages((prev) => prev.filter((m) => m !== tempUserMessage));

      if (error.response?.status === 503) {
        toast.error(t.aiUnavailable + ": " + t.aiUnavailableMessage);
      } else {
        toast.error("Failed to send message");
      }
    } finally {
      setIsSending(false);
      inputRef.current?.focus();
    }
  };

  const handleNewConversation = () => {
    setCurrentConversation(null);
    setMessages([]);
    setInputMessage("");
    inputRef.current?.focus();
  };

  const handleDeleteConversation = async (conversationId: string) => {
    if (!confirm(t.deleteConversation + "?")) return;

    try {
      await chatService.deleteConversation(conversationId);
      setConversations((prev) => prev.filter((c) => c._id !== conversationId));

      if (currentConversation === conversationId) {
        handleNewConversation();
      }

      toast.success(t.conversationDeleted);
    } catch (error: any) {
      console.error("Failed to delete conversation:", error);
      toast.error("Failed to delete conversation");
    }
  };

  const formatMessageTime = (timestamp: Date) => fmt.time(timestamp);

  const exampleQuestions = [t.example1, t.example2, t.example3];

  return (
    <div className="flex h-[calc(100vh-4rem)] bg-gray-50 dark:bg-gray-900">
      {/* Sidebar - Always visible */}
      <div className="w-80 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 flex flex-col">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <button
            onClick={handleNewConversation}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium"
          >
            <Plus className="w-5 h-5" />
            {t.newConversation}
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {isLoading && conversations.length === 0 ? (
            <div className="flex items-center justify-center h-32">
              <Loader2 className="w-6 h-6 text-gray-400 animate-spin" />
            </div>
          ) : conversations.length === 0 ? (
            <div className="p-6 text-center text-gray-500 dark:text-gray-400">
              <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p className="text-sm">{t.noConversationsYet}</p>
              <p className="text-xs mt-1">{t.startNewConversation}</p>
            </div>
          ) : (
            <div className="p-2 space-y-1">
              {conversations.map((conv) => (
                <button
                  key={conv._id}
                  onClick={() => setCurrentConversation(conv._id)}
                  className={`w-full flex items-start gap-3 p-3 rounded-lg transition-colors text-left group ${
                    currentConversation === conv._id
                      ? "bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800"
                      : "hover:bg-gray-50 dark:hover:bg-gray-700/50"
                  }`}
                >
                  <MessageSquare
                    className={`w-5 h-5 mt-0.5 flex-shrink-0 ${
                      currentConversation === conv._id
                        ? "text-blue-600 dark:text-blue-400"
                        : "text-gray-400"
                    }`}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                      {conv.title}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {fmt.date(conv.updatedAt)}
                    </p>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteConversation(conv._id);
                    }}
                    className="opacity-0 group-hover:opacity-100 p-1 hover:bg-red-100 dark:hover:bg-red-900/30 rounded transition-all"
                  >
                    <Trash2 className="w-4 h-4 text-red-600 dark:text-red-400" />
                  </button>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="p-4 border-t border-gray-200 dark:border-gray-700">
          <label
            htmlFor="include-health-context"
            className="flex items-start gap-3 cursor-pointer group"
            aria-label="Include health context in conversation"
          >
            <input
              id="include-health-context"
              type="checkbox"
              checked={includeHealthContext}
              onChange={(e) => setIncludeHealthContext(e.target.checked)}
              className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              aria-label="Include health context"
            />
            <div className="flex-1">
              <div className="flex items-center gap-2">
                {includeHealthContext ? (
                  <BrainCircuit className="w-4 h-4 text-blue-600" />
                ) : (
                  <Brain className="w-4 h-4 text-gray-400" />
                )}
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  {t.includeHealthContext}
                </span>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {includeHealthContext
                  ? t.healthContextEnabled
                  : t.healthContextDisabled}
              </p>
            </div>
          </label>
        </div>
      </div>
      {/* End Sidebar */}

      <div className="flex-1 flex flex-col">
        <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center">
                <Bot className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {t.tinaHealthAssistant}
                </h1>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t.askMeAnything}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {messages.length === 0 && !currentConversation ? (
            <div className="max-w-2xl mx-auto text-center py-12">
              <Bot className="w-16 h-16 mx-auto mb-4 text-blue-500" />
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                {t.chatWithTina}
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mb-8">
                {t.askMeAnything}
              </p>

              <div className="text-left">
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  {t.exampleQuestions}
                </p>
                <div className="space-y-2">
                  {exampleQuestions.map((question, index) => (
                    <button
                      key={index}
                      onClick={() => setInputMessage(question)}
                      className="w-full text-left p-3 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors text-sm text-gray-700 dark:text-gray-300"
                    >
                      {question}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <>
              {messages.map((message, index) => (
                <div
                  key={index}
                  className={`flex gap-3 ${message.role === "user" ? "justify-end" : "justify-start"}`}
                >
                  {message.role === "assistant" && (
                    <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center flex-shrink-0">
                      <Bot className="w-5 h-5 text-white" />
                    </div>
                  )}

                  <div
                    className={`max-w-[70%] rounded-lg p-4 ${
                      message.role === "user"
                        ? "bg-blue-600 text-white"
                        : "bg-white dark:bg-gray-800 text-gray-900 dark:text-white border border-gray-200 dark:border-gray-700"
                    }`}
                  >
                    <p className="text-sm whitespace-pre-wrap">
                      {message.content}
                    </p>
                    <p
                      className={`text-xs mt-2 ${
                        message.role === "user"
                          ? "text-blue-100"
                          : "text-gray-500 dark:text-gray-400"
                      }`}
                    >
                      {formatMessageTime(message.timestamp)}
                    </p>
                  </div>

                  {message.role === "user" && (
                    <div className="w-8 h-8 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center flex-shrink-0">
                      <User className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                    </div>
                  )}
                </div>
              ))}

              {isSending && (
                <div className="flex gap-3">
                  <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center flex-shrink-0">
                    <Bot className="w-5 h-5 text-white" />
                  </div>
                  <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                    <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span className="text-sm">{t.aiThinking}</span>
                    </div>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </>
          )}
        </div>

        <div className="bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 p-4">
          <form onSubmit={handleSendMessage} className="max-w-4xl mx-auto">
            <div className="flex gap-2">
              <input
                ref={inputRef}
                type="text"
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                placeholder={t.messagePlaceholder}
                disabled={isSending}
                className="flex-1 px-4 py-3 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
              />
              <button
                type="submit"
                disabled={!inputMessage.trim() || isSending}
                className="px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 dark:disabled:bg-gray-600 text-white rounded-lg transition-colors disabled:cursor-not-allowed flex items-center gap-2 font-medium"
              >
                {isSending ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Send className="w-5 h-5" />
                )}
                <span className="hidden sm:inline">{t.sendMessage}</span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ChatPage;
